import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const INTENTS = [
  {
    id: 'get_signals',
    keywords: ['how can i get crypto signals', 'get crypto signals', 'how to get signals', 'where can i get signals'],
    answer:
      'You can get crypto signals directly in the Trend Genie app. Trend Genie is an AI-powered crypto ecosystem that provides structured signals with Entry, TP, and SL. Download the app here: https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie'
  },
  {
    id: 'join',
    keywords: ['how to join', 'join trend genie', 'start using app', 'get started', 'sign up', 'signup', 'register'],
    answer:
      'To get started: 1) Download Trend Genie app 2) Create your account 3) Choose your plan 4) Start using signals and tools. App link: https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie'
  },
  {
    id: 'free_or_paid',
    keywords: ['is it free', 'free or paid', 'subscription', 'charges', 'monthly fee', 'paid plan', 'trial'],
    answer:
      'Trend Genie offers plan-based access. You can view current packages and details here: https://www.trendgenie.io/pricing'
  },
  {
    id: 'accuracy',
    keywords: ['accuracy', 'accurate', 'win rate', 'success rate', 'how accurate are signals', 'signal accuracy'],
    answer:
      'Signal outcomes vary with market conditions and risk management. You can review real performance here: https://www.trendgenie.io/live-results'
  },
  {
    id: 'account_help',
    keywords: ['login issue', 'cannot login', 'account issue', 'support', 'customer support', 'help center'],
    answer:
      'If you face account or app issues, share details through the contact form on site and our team will assist you quickly.'
  },
  {
    id: 'platform',
    keywords: ['ios', 'iphone app', 'apple store', 'is there ios', 'which platforms'],
    answer:
      'Right now, the public app link available is Android (Google Play): https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie'
  },
  {
    id: 'greeting',
    keywords: ['hi', 'hello', 'salam', 'hey', 'aoa', 'assalam'],
    answer:
      'Hi! I can help with Trend Genie app, signals, RugShield, TGEN, pricing, rewards, and general crypto basics.'
  },
  {
    id: 'about',
    keywords: ['what is trend genie', 'about trend genie', 'trend genie', 'company'],
    answer:
      'Trend Genie is an AI-powered crypto ecosystem with trading signals, DeFi safety tools, community products, and the TGEN utility token.'
  },
  {
    id: 'download',
    keywords: ['download', 'install', 'android', 'play store', 'app link'],
    answer:
      'You can download the app from Google Play: https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie'
  },
  {
    id: 'signals',
    keywords: ['signal', 'signals', 'entry', 'tp', 'sl', 'stop loss', 'take profit'],
    answer:
      'Trend Genie signals include Entry, Stop Loss, and Take Profit levels. Use risk management and avoid over-leverage on any single trade.'
  },
  {
    id: 'rugshield',
    keywords: ['rugshield', 'scanner', 'defi safety', 'honeypot', 'rug pull'],
    answer:
      'RugShield helps identify DeFi token risks like suspicious contracts, honeypot behavior, and red-flag token patterns.'
  },
  {
    id: 'social',
    keywords: ['social', 'community', 'trend genie social'],
    answer:
      'Trend Genie Social is the community side of the ecosystem where users share updates, discussions, and market insights.'
  },
  {
    id: 'tgen',
    keywords: ['tgen', 'token', 'utility token', 'coin'],
    answer:
      'TGEN is the utility token of the Trend Genie ecosystem, designed for product access, usage utility, and ecosystem participation.'
  },
  {
    id: 'pricing',
    keywords: ['pricing', 'price plan', 'subscription', 'plans', 'cost'],
    answer:
      'You can check pricing plans here: https://www.trendgenie.io/pricing'
  },
  {
    id: 'rewards',
    keywords: ['rewards', 'airdrop', 'bonus'],
    answer:
      'You can view rewards and airdrop details here: https://www.trendgenie.io/rewards'
  },
  {
    id: 'results',
    keywords: ['live result', 'performance', 'win rate', 'results'],
    answer:
      'Live performance and win-rate details are available here: https://www.trendgenie.io/live-results'
  },
  {
    id: 'whitepaper',
    keywords: ['whitepaper', 'docs', 'documentation'],
    answer:
      'You can read the official whitepaper here: https://www.trendgenie.io/whitepaper'
  },
  {
    id: 'privacy',
    keywords: ['privacy', 'policy', 'terms'],
    answer:
      'Privacy policy is available at: https://www.trendgenie.io/privacy-policy'
  },
  {
    id: 'crypto_basic',
    keywords: ['what is crypto', 'blockchain', 'bitcoin', 'altcoin', 'defi', 'wallet'],
    answer:
      'Crypto is a digital asset ecosystem built on blockchain networks. If you want, I can explain basics step-by-step like wallet, exchange, and risk management.'
  },
  {
    id: 'risk_management',
    keywords: ['risk', 'risk management', 'leverage', 'position size', 'capital', 'loss'],
    answer:
      'Basic risk rule: risk only a small portion per trade (commonly 1-2%), always use stop loss, and avoid emotional re-entry after losses.'
  },
  {
    id: 'market_price',
    keywords: ['current price', 'live price', 'now price', 'price now', 'today price'],
    answer:
      'I cannot fetch live market prices in this offline mode yet. Once LLM/API is connected, I can provide real-time data from your configured sources.'
  },
  {
    id: 'thanks',
    keywords: ['thanks', 'thank you', 'shukriya', 'jazakallah'],
    answer:
      'You are welcome. If you want, ask me about app features, signals, TGEN token, or trading basics.'
  }
];

const BRAND_HINT =
  'I can help with Trend Genie app/products and crypto basics. If your query is unrelated, share name/email and our team will contact you.';

function normalize(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text) {
  return normalize(text).split(' ').filter(Boolean);
}

function scoreIntent(question, intent) {
  const q = normalize(question);
  const tokens = tokenize(question);
  let score = 0;

  intent.keywords.forEach((kw) => {
    const keyword = normalize(kw);
    if (!keyword) return;
    if (q.includes(keyword)) {
      score += keyword.split(' ').length > 1 ? 3 : 2;
      return;
    }
    const parts = keyword.split(' ');
    const overlap = parts.filter((p) => tokens.includes(p)).length;
    if (overlap > 0) score += overlap;
  });

  return score;
}

function getLocalAnswer(question) {
  const cleaned = normalize(question);
  if (!cleaned) return null;

  let best = null;
  let bestScore = 0;

  INTENTS.forEach((intent) => {
    const score = scoreIntent(cleaned, intent);
    if (score > bestScore) {
      best = intent;
      bestScore = score;
    }
  });

  if (best && bestScore >= 2) {
    return { type: 'answer', text: best.answer };
  }

  if (cleaned.length <= 4) {
    return { type: 'answer', text: 'Please type a bit more detail so I can help properly.' };
  }

  if (cleaned.includes('help')) {
    return { type: 'answer', text: BRAND_HINT };
  }

  return null;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

const ChatBotWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'm1',
      role: 'bot',
      text: 'Hi, I\'m Trend Genie Assistant. Ask me about app/products or crypto basics.'
    }
  ]);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [pendingQuestion, setPendingQuestion] = useState('');
  const [submittingLead, setSubmittingLead] = useState(false);

  const canSend = useMemo(() => input.trim().length > 0, [input]);

  const appendBot = (text) => {
    setMessages((prev) => [...prev, { id: `b-${Date.now()}-${Math.random()}`, role: 'bot', text }]);
  };

  const appendUser = (text) => {
    setMessages((prev) => [...prev, { id: `u-${Date.now()}-${Math.random()}`, role: 'user', text }]);
  };

  const handleSend = () => {
    const question = input.trim();
    if (!question) return;

    appendUser(question);
    setInput('');

    const local = getLocalAnswer(question);
    if (local?.type === 'answer') {
      appendBot(local.text);
      setShowLeadForm(false);
      return;
    }

    setPendingQuestion(question);
    setShowLeadForm(true);
    appendBot('This looks outside my current scope. Please share your name and email below, and we will contact you soon.');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const name = leadName.trim();
    const email = leadEmail.trim();

    if (!name || !email) return;
    if (!isValidEmail(email)) {
      appendBot('Please enter a valid email address.');
      return;
    }

    setSubmittingLead(true);

    try {
      const existing = JSON.parse(localStorage.getItem('tg_chatbot_leads') || '[]');
      const updated = [
        ...existing,
        {
          name,
          email,
          question: pendingQuestion,
          source: 'website_chatbot',
          createdAt: new Date().toISOString()
        }
      ];
      localStorage.setItem('tg_chatbot_leads', JSON.stringify(updated));
      appendBot('Thank you. We have received your details and we will contact you soon.');
    } catch (err) {
      appendBot('Thank you. We have your request and our team will contact you soon.');
    } finally {
      setShowLeadForm(false);
      setLeadName('');
      setLeadEmail('');
      setPendingQuestion('');
      setSubmittingLead(false);
    }
  };

  return (
    <Wrap>
      {open && (
        <Panel>
          <Header>
            <Title>Trend Genie Assistant</Title>
            <CloseBtn onClick={() => setOpen(false)} type="button">Close</CloseBtn>
          </Header>

          <Messages>
            {messages.map((m) => (
              <Bubble key={m.id} role={m.role}>
                {m.text}
              </Bubble>
            ))}
          </Messages>

          {showLeadForm && (
            <LeadForm onSubmit={handleLeadSubmit}>
              <LeadTitle>Contact Form</LeadTitle>
              <LeadInput
                type="text"
                placeholder="Your name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                required
              />
              <LeadInput
                type="email"
                placeholder="Your email"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                required
              />
              <LeadSubmit type="submit" disabled={submittingLead}>
                {submittingLead ? 'Submitting...' : 'Submit'}
              </LeadSubmit>
            </LeadForm>
          )}

          <InputRow>
            <TextInput
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about app, products, token, crypto basics..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <SendBtn type="button" onClick={handleSend} disabled={!canSend}>
              Send
            </SendBtn>
          </InputRow>
        </Panel>
      )}

      <Toggle type="button" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide Chat' : 'Ask AI'}
      </Toggle>
    </Wrap>
  );
};

export default ChatBotWidget;

const Wrap = styled.div`
  position: fixed;
  right: 18px;
  bottom: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.7rem;
`;

const Toggle = styled.button`
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: #fff;
  font-weight: 700;
  padding: 0.78rem 1.1rem;
  cursor: pointer;
  box-shadow: var(--shadow-primary);
`;

const Panel = styled.div`
  width: min(360px, calc(100vw - 22px));
  max-height: min(78vh, 620px);
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
`;

const Header = styled.div`
  padding: 0.72rem 0.82rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;

const Title = styled.div`
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--text-color);
`;

const CloseBtn = styled.button`
  border: 1px solid var(--border-color);
  background: var(--bg-surface);
  border-radius: 999px;
  padding: 0.24rem 0.6rem;
  cursor: pointer;
  color: var(--text-secondary-color);
  font-size: 0.72rem;
`;

const Messages = styled.div`
  padding: 0.8rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.48rem;
  min-height: 220px;
`;

const Bubble = styled.div`
  max-width: 90%;
  align-self: ${(p) => (p.role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${(p) => (p.role === 'user' ? 'var(--primary-light)' : 'var(--bg-secondary)')};
  color: var(--text-color);
  border: 1px solid var(--border-color);
  padding: 0.55rem 0.65rem;
  border-radius: 12px;
  font-size: 0.83rem;
  line-height: 1.5;
`;

const LeadForm = styled.form`
  margin: 0 0.8rem 0.65rem;
  padding: 0.65rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--bg-secondary);
  display: grid;
  gap: 0.42rem;
`;

const LeadTitle = styled.div`
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--text-color);
`;

const LeadInput = styled.input`
  width: 100%;
  border: 1px solid var(--border-color);
  border-radius: 9px;
  padding: 0.52rem 0.6rem;
  font-size: 0.82rem;
  outline: none;
  background: var(--bg-surface);
  color: var(--text-color);
`;

const LeadSubmit = styled.button`
  border: none;
  border-radius: 9px;
  padding: 0.58rem 0.7rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.7 : 1)};
`;

const InputRow = styled.div`
  padding: 0.7rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.45rem;
`;

const TextInput = styled.input`
  flex: 1;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.58rem 0.66rem;
  font-size: 0.82rem;
  outline: none;
  color: var(--text-color);
  background: var(--bg-surface);
`;

const SendBtn = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.58rem 0.76rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.65 : 1)};
`;
